import { concat, fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { htmlSafe } from '@ember/template';
import type { SafeString } from '@ember/template/-private/handlebars';
import { tracked } from '@glimmer/tracking';
import { queue } from '@nullvoxpopuli/ember-composable-helpers';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import { and, or } from 'ember-truth-helpers';
import ProfilePhotoUploadModal from 'garaje/components/employees/profile-photo/uploader/modal';
import MonogramOrPhoto from 'garaje/components/monogram-or-photo';
import ConfirmationModal from 'garaje/components/ui/classic/confirmation-modal';
import { localCopy } from 'tracked-toolbox';

const DEFAULT_SIZE = 5;

interface EmployeesProfilePhotoArguments {
  /**
   * Indicate photo is display only (no editing)
   */
  isDisabled?: boolean;
  isLoading?: boolean;
  /**
   * Diameter of photo element (in rem, default: 5)
   */
  size?: number | string;
  /**
   * URL of photo (inherited from MonogramOrPhoto)
   */
  devicePhoto?: string;
  /**
   * URL of photo (inherited from MonogramOrPhoto)
   */
  originalDevicePhoto?: string;
  /**
   * URL of photo (inherited from MonogramOrPhoto)
   */
  passportPhoto?: string | null;
  /**
   * Disable upload functionality - hides camera icon and disables click functionality
   */
  uploadDisabled?: boolean;
  /**
   * Show remove photo button
   */
  showRemoveButton?: boolean;
  /**
   * Show close button in upload modal
   */
  showCloseButton?: boolean;
  /**
   * Action for handling errors (passes through to modal/input)
   */
  onError?: (error: unknown) => void;
  /**
   * Size of square output image in px (passes through to modal/input)
   */
  outputSize?: number;
  /**
   *  Custom validation failure message (passes through to modal/input)
   */
  validationError?: string;
  /**
   *  Allowed file types (passes through to modal/input)
   */
  validType?: string | RegExp;
  onUploadComplete?: (result: unknown) => void;
  /**
   * Action to call when photo is deleted
   */
  onDelete?: () => void;
  id: string;
}

export default class EmployeesProfilePhoto extends MonogramOrPhoto<EmployeesProfilePhotoArguments> {
  @localCopy('args.size', DEFAULT_SIZE) declare size: this['args']['size'];
  @tracked showUploadModal = false;
  @tracked showDeleteModal = false;
  @tracked invalidThumbnailPaths: Record<string, boolean> = {};

  get safeSize(): number {
    return Math.max(parseFloat(`${this.size}`) || DEFAULT_SIZE, 1);
  }

  get safeStyle(): SafeString {
    const { safeSize, monogramHueValue, monogramInitials } = this;
    const monogramLength = Math.max(2, monogramInitials.length);
    const fontSize = safeSize / monogramLength;
    const sizeStyle = `width: ${safeSize}rem; height: ${safeSize}rem;`;
    const textStyle = `line-height: ${safeSize + 0.2}rem; font-size: ${fontSize}rem;`;
    const bgStyle = `background-color: hsl(${monogramHueValue}, 33%, 45%);`;

    return htmlSafe(`${sizeStyle} ${textStyle} ${bgStyle}`);
  }

  get isUploadModalVisible(): boolean {
    if (!this.showUploadModal) return false;
    if (this.args.isDisabled) return false;
    if (this.showDeleteModal) return false;

    return true;
  }

  get svgIconName(): string | null {
    if (this.args.isDisabled) {
      return null;
    }

    if (this.args.isLoading) {
      return 'progress-small-white';
    }

    if (this.thumbnailPath) {
      return 'pencil-white-full';
    }

    // Hide camera icon if uploadDisabled is true (only when no photo)
    if (this.args.uploadDisabled) {
      return null;
    }

    return 'camera-white';
  }

  get isValidImage(): boolean {
    return Boolean(this.thumbnailPath && !this.invalidThumbnailPaths[this.thumbnailPath.toString()]);
  }

  get shouldDisableClick(): boolean {
    // Only disable click when uploadDisabled is true AND there's no photo
    // If there's a photo, allow click to open modal (even in uploadDisabled mode)
    return Boolean(this.args.uploadDisabled) && !this.thumbnailPath;
  }

  @action
  openUploadModal(): void {
    if (this.args.isDisabled) return;
    if (this.args.isLoading) return;
    if (this.shouldDisableClick) return;

    this.showUploadModal = true;
  }

  // Thumbnail path is marked invalid if image loading fails
  // (e.g. image src URL returns 404 not found)
  @action
  handleImageError(): void {
    const { thumbnailPath } = this;

    if (thumbnailPath) {
      this.invalidThumbnailPaths = { [thumbnailPath.toString()]: true };
    }
  }

  @action
  confirmDelete(): void {
    if (this.args.onDelete) {
      this.args.onDelete();
    }
    this.showDeleteModal = false;
    this.showUploadModal = false;
  }

  @action
  cancelDelete(): void {
    this.showDeleteModal = false;
    this.showUploadModal = false;
  }
  <template>
    <div
      class="relative {{if (or @isDisabled @isLoading this.shouldDisableClick) 'cursor-default' 'cursor-pointer'}}"
      {{on "click" this.openUploadModal}}
      data-test-employee-profile-photo
      ...attributes
    >
      {{#if (has-block)}}
        {{yield}}
      {{else}}
        {{#if (and this.thumbnailPath this.isValidImage)}}
          <div class="relative mx-auto rounded-full overflow-hidden" style={{this.safeStyle}} data-test-employee-photo>
            <img
              alt=""
              class="object-cover"
              src={{this.thumbnailPath}}
              role="presentation"
              {{on "error" this.handleImageError}}
            />

            {{#if this.svgIconName}}
              <div
                role="presentation"
                class="absolute bottom-0 w-full h-6 leading-4 pt-1px text-center bg-carbon-80/50 pointer-events-none"
              >
                {{svgJar this.svgIconName class="icon" width="16px" height="16px"}}
              </div>
            {{/if}}
          </div>
        {{else}}
          <div
            class="relative mx-auto text-center text-white rounded-full whitespace-nowrap overflow-hidden
              {{this.monogramClasses}}
              {{this.lengthClass}}"
            style={{this.safeStyle}}
            data-test-employee-monogram
          >
            {{this.monogramInitials}}

            {{#if this.svgIconName}}
              <div
                role="presentation"
                class="absolute bottom-0 w-full h-6 leading-4 pt-0.5 text-center bg-carbon-80/50 pointer-events-none"
              >
                {{svgJar this.svgIconName class="icon" width="16px" height="16px"}}
              </div>
            {{/if}}
          </div>
        {{/if}}
      {{/if}}
    </div>

    {{#if this.isUploadModalVisible}}
      <ProfilePhotoUploadModal
        @prefix={{concat "user-documents/users/" @id}}
        @isLoading={{@isLoading}}
        @currentPhoto={{this.thumbnailPath}}
        @onUploadComplete={{queue @onUploadComplete (fn (mut this.showUploadModal) false)}}
        @onClose={{fn (mut this.showUploadModal) false}}
        @onError={{@onError}}
        @outputSize={{@outputSize}}
        @validationError={{@validationError}}
        @validType={{@validType}}
        @uploadDisabled={{@uploadDisabled}}
        @onRemoveRequest={{fn (mut this.showDeleteModal) true}}
        @showRemoveButton={{@showRemoveButton}}
        @showCloseButton={{@showCloseButton}}
      />
    {{/if}}

    {{#if this.showDeleteModal}}
      <ConfirmationModal
        @title="Are you sure you want to delete this photo?"
        @ctaText="Delete"
        @cancelText="Cancel"
        @onClose={{this.cancelDelete}}
        @handleTaskAction={{this.confirmDelete}}
        @showTopRightCloseButton={{true}}
      >
        <p class="mt-4 text-base">
          Once deleted, it cannot be restored.
        </p>
      </ConfirmationModal>
    {{/if}}
  </template>
}
